import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    Box,
    CircularProgress,
    Divider,
    IconButton,
    Popover,
    Tooltip,
    Typography,
    alpha,
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import http from '../../services/http';
import useEstouMontado from '../../hooks/useEstouMontado';
import { ThemeContext } from '../ThemeContext/ThemeContext';

function formatDuration(totalSeconds) {
    const s = Math.max(0, Math.floor(totalSeconds || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) {
        return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    }
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function sessionSecondsFromDates(session) {
    if (!session?.started_at) return 0;
    const start = new Date(session.started_at).getTime();
    if (Number.isNaN(start)) return 0;
    const end = session.ended_at ? new Date(session.ended_at).getTime() : Date.now();
    if (Number.isNaN(end)) return Math.max(0, Math.floor((Date.now() - start) / 1000));
    return Math.max(0, Math.floor((end - start) / 1000));
}

function computeUserTotalSeconds(userData, tick = 0) {
    if (!userData) return 0;
    void tick;
    const sessions = userData.sessions || [];
    if (sessions.length > 0) {
        return sessions.reduce((acc, s) => {
            if (s.is_active) return acc + sessionSecondsFromDates(s);
            const fromApi = Number(s.duration_seconds);
            if (fromApi > 0) return acc + fromApi;
            return acc + sessionSecondsFromDates(s);
        }, 0);
    }
    return Number(userData.total_seconds) || 0;
}

const TicketAttendanceTimer = ({ ticketId, currentUserId, assignedToId, canManage, refreshKey = 0 }) => {
    const { theme } = useContext(ThemeContext);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [tick, setTick] = useState(0);
    const [teamAnchor, setTeamAnchor] = useState(null);

    const isAssignee = currentUserId != null && Number(assignedToId) === Number(currentUserId);
    const canControlTimer = isAssignee && canManage;

    // Fechar o chamado enquanto o resumo está sendo buscado deixava
    // "state update on an unmounted component" no console.
    const estouMontado = useEstouMontado();

    const applySummary = useCallback((data) => {
        if (!estouMontado()) return;
        if (data?.users) setSummary(data);
        else if (data?.summary?.users) setSummary(data.summary);
    }, [estouMontado]);

    const fetchSummary = useCallback(async () => {
        if (!ticketId || ticketId === 'new') return null;
        setLoading(true);
        try {
            const res = await http.get(`/ticket/${ticketId}/attendance`);
            applySummary(res.data);
            return res.data;
        } catch {
            return null;
        } finally {
            if (estouMontado()) setLoading(false);
        }
    }, [ticketId, applySummary, estouMontado]);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary, refreshKey]);

    const myUserData = useMemo(() => {
        if (!summary?.users || currentUserId == null) return null;
        return summary.users.find((u) => Number(u.user_id) === Number(currentUserId)) || null;
    }, [summary, currentUserId]);

    const hasActiveSession = Boolean(myUserData?.active_session);

    useEffect(() => {
        if (!hasActiveSession) return undefined;
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, [hasActiveSession]);

    const displaySeconds = useMemo(
        () => computeUserTotalSeconds(myUserData, tick),
        [myUserData, tick]
    );

    const otherUsers = useMemo(
        () => (summary?.users || []).filter(
            (u) => Number(u.user_id) !== Number(currentUserId) && computeUserTotalSeconds(u) > 0
        ),
        [summary, currentUserId, tick]
    );

    const handleStart = async () => {
        setActionLoading(true);
        try {
            const res = await http.post(`/ticket/${ticketId}/attendance/start`);
            applySummary(res.data?.summary ?? res.data);
        } finally {
            if (estouMontado()) setActionLoading(false);
        }
    };

    const handlePause = async () => {
        setActionLoading(true);
        try {
            const res = await http.post(`/ticket/${ticketId}/attendance/pause`);
            applySummary(res.data?.summary ?? res.data);
        } finally {
            if (estouMontado()) setActionLoading(false);
        }
    };

    if (!ticketId || ticketId === 'new') return null;

    const showWidget = canControlTimer || displaySeconds > 0 || hasActiveSession || otherUsers.length > 0;
    if (!showWidget && !loading) return null;

    const statusLabel = hasActiveSession
        ? 'Em atendimento'
        : displaySeconds > 0
            ? 'Pausado'
            : isAssignee
                ? 'Pronto para iniciar'
                : 'Sem tempo';

    const dotColor = hasActiveSession
        ? theme.palette.success.main
        : displaySeconds > 0
            ? theme.palette.warning.main
            : theme.palette.text.disabled;

    const pillBorder = hasActiveSession
        ? theme.palette.primary.main
        : theme.palette.divider;

    const pillBg = hasActiveSession
        ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.08)
        : theme.palette.background.paper;

    return (
        <>
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    pl: 1,
                    pr: 0.25,
                    py: 0.25,
                    borderRadius: 'var(--radius-md, 10px)',
                    border: '1px solid',
                    borderColor: pillBorder,
                    bgcolor: pillBg,
                    boxShadow: hasActiveSession
                        ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.25)}, 0 2px 8px ${alpha(theme.palette.primary.main, 0.12)}`
                        : 'none',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
                    flexShrink: 0,
                }}
            >
                <TimerOutlinedIcon
                    sx={{
                        fontSize: 16,
                        color: hasActiveSession ? 'primary.main' : 'text.secondary',
                        mr: 0.25,
                    }}
                />

                <Box
                    sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        bgcolor: dotColor,
                        flexShrink: 0,
                        mr: 0.5,
                        ...(hasActiveSession && {
                            boxShadow: `0 0 0 2px ${alpha(theme.palette.success.main, 0.35)}`,
                            '@keyframes attendancePulse': {
                                '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                                '50%': { opacity: 0.55, transform: 'scale(0.85)' },
                            },
                            animation: 'attendancePulse 1.4s ease-in-out infinite',
                        }),
                    }}
                />

                <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1, mr: 0.25, minWidth: 52 }}>
                    <Typography
                        component="span"
                        sx={{
                            fontSize: '0.68rem',
                            fontWeight: 600,
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                        }}
                    >
                        {statusLabel}
                    </Typography>
                    {loading ? (
                        <CircularProgress size={12} sx={{ mt: 0.25 }} />
                    ) : (
                        <Typography
                            component="span"
                            sx={{
                                fontSize: '0.95rem',
                                fontWeight: 700,
                                fontVariantNumeric: 'tabular-nums',
                                color: hasActiveSession ? 'primary.main' : 'text.primary',
                                letterSpacing: '0.02em',
                            }}
                        >
                            {formatDuration(displaySeconds)}
                        </Typography>
                    )}
                </Box>

                {canControlTimer && !loading && (
                    <Tooltip
                        title={
                            hasActiveSession
                                ? 'Pausar atendimento'
                                : displaySeconds > 0
                                    ? 'Retomar atendimento'
                                    : 'Iniciar atendimento manualmente'
                        }
                        arrow
                    >
                        <span>
                            <IconButton
                                size="small"
                                disabled={actionLoading}
                                onClick={hasActiveSession ? handlePause : handleStart}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    color: hasActiveSession ? 'text.secondary' : 'primary.main',
                                    bgcolor: hasActiveSession
                                        ? 'transparent'
                                        : alpha(theme.palette.primary.main, 0.12),
                                    '&:hover': {
                                        bgcolor: hasActiveSession
                                            ? 'action.hover'
                                            : alpha(theme.palette.primary.main, 0.2),
                                    },
                                }}
                            >
                                {actionLoading ? (
                                    <CircularProgress size={14} color="inherit" />
                                ) : hasActiveSession ? (
                                    <PauseRoundedIcon sx={{ fontSize: 18 }} />
                                ) : (
                                    <PlayArrowRoundedIcon sx={{ fontSize: 20 }} />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                )}

                {otherUsers.length > 0 && (
                    <Tooltip title="Tempo dos demais envolvidos" arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => setTeamAnchor(e.currentTarget)}
                            sx={{ width: 26, height: 26, color: 'text.secondary' }}
                        >
                            <GroupsOutlinedIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <Popover
                open={Boolean(teamAnchor)}
                anchorEl={teamAnchor}
                onClose={() => setTeamAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        mt: 0.75,
                        borderRadius: 'var(--radius-md, 10px)',
                        border: '1px solid',
                        borderColor: 'divider',
                        minWidth: 200,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    },
                }}
            >
                <Box sx={{ px: 1.5, py: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                        Tempo por pessoa
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ py: 0.5 }}>
                    {myUserData && displaySeconds > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                px: 1.5,
                                py: 0.75,
                                bgcolor: alpha(theme.palette.primary.main, 0.06),
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Você
                            </Typography>
                            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                                {formatDuration(displaySeconds)}
                                {hasActiveSession ? ' ●' : ''}
                            </Typography>
                        </Box>
                    )}
                    {otherUsers.map((u) => (
                        <Box
                            key={u.user_id}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                px: 1.5,
                                py: 0.75,
                            }}
                        >
                            <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                                {u.user_name || `Usuário #${u.user_id}`}
                            </Typography>
                            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                                {formatDuration(computeUserTotalSeconds(u, tick))}
                                {u.active_session ? ' ●' : ''}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Popover>
        </>
    );
};

export default TicketAttendanceTimer;
